import { useState, useEffect, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { listUsersSimple } from "../../services/users.api";
import {
  getOnboardingConfig,
  activateOnboarding,
  listOnboardingSequences,
  createOnboardingSequence,
  updateOnboardingSequence,
  deleteOnboardingSequence,
  createOnboardingService,
  updateOnboardingService,
  deleteOnboardingService,
  createOnboardingProfessional,
  updateOnboardingProfessional,
  deleteOnboardingProfessional,
  updateOnboardingBusinessHours,
  updateOnboardingSequenceByKey,
  updateOnboardingProfessionalUser,
  patchOnboardingConfig,
} from "../../services/onboarding.api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import {
  CONFIG_CARD_DEFS,
  SEQ_CATEGORIES,
  TRIGGER_UNITS,
  TRIGGER_DIRS,
  SEQ_DIALOG_EMPTY,
  SEQ_VARS_HINT,
  SVC_DIALOG_EMPTY,
  PROF_DIALOG_EMPTY,
  AGENT_SEQ_KEYS,
  DAY_NAMES,
  SLOT_OPTIONS,
  DEFAULT_DAYS,
  AVATAR_COLORS,
  BUSINESS_DIALOG_EMPTY,
} from "./appointments.config.constants";

// D8.7.3 — Lógica (estados + effects + handlers + API) de AppointmentsConfig.
export default function useAppointmentsConfig() {
  const history = useHistory();

  // Nav
  const [submenuIndex, setSubmenuIndex] = useState(0);

  // Perfil
  const [profile, setProfile]   = useState(null);
  const [profileId, setProfileId] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [recrearKanbanLoading, setRecrearKanbanLoading] = useState(false);

  // Datos del negocio (edición no destructiva vía PATCH /onboarding/config)
  const [businessData, setBusinessData] = useState(BUSINESS_DIALOG_EMPTY);
  const [businessDialogOpen, setBusinessDialogOpen] = useState(false);
  const [businessDialogSaving, setBusinessDialogSaving] = useState(false);

  // Servicios
  const [services, setServices]               = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);

  // Equipo
  const [professionals, setProfessionals] = useState([]);
  const [users, setUsers]                 = useState([]);
  const [equipoLoading, setEquipoLoading] = useState(false);

  // Horarios
  const [horariosLocal, setHorariosLocal]       = useState(DEFAULT_DAYS);
  const [globalSlot, setGlobalSlot]             = useState(60);
  const [horariosLoading, setHorariosLoading]   = useState(false);
  const [horariosModified, setHorariosModified] = useState(false);
  const [horariosSaving, setHorariosSaving]     = useState(false);

  // Secuencias
  const [sequences, setSequences]   = useState([]);
  const [seqLoading, setSeqLoading] = useState(false);

  // Dialogo CRUD secuencia
  const [seqDialogOpen, setSeqDialogOpen]     = useState(false);
  const [seqDialogMode, setSeqDialogMode]     = useState("create");
  const [seqDialogData, setSeqDialogData]     = useState(SEQ_DIALOG_EMPTY);
  const [seqDialogSaving, setSeqDialogSaving] = useState(false);
  const [seqDeleteTarget, setSeqDeleteTarget] = useState(null);

  // Dialogo CRUD servicio
  const [svcDialogOpen, setSvcDialogOpen]     = useState(false);
  const [svcDialogMode, setSvcDialogMode]     = useState("create");
  const [svcDialogData, setSvcDialogData]     = useState(SVC_DIALOG_EMPTY);
  const [svcDialogSaving, setSvcDialogSaving] = useState(false);
  const [svcDeleteTarget, setSvcDeleteTarget] = useState(null);

  // Dialogo CRUD profesional
  const [profDialogOpen, setProfDialogOpen]     = useState(false);
  const [profDialogMode, setProfDialogMode]     = useState("create");
  const [profDialogData, setProfDialogData]     = useState(PROF_DIALOG_EMPTY);
  const [profDialogSaving, setProfDialogSaving] = useState(false);
  const [profDeleteTarget, setProfDeleteTarget] = useState(null);

  // Agente IA

  const configCards = useMemo(
    () => CONFIG_CARD_DEFS.map((c) => ({
      ...c,
      title: i18n.t(`appointmentsModule.config.${c.titleKey}`),
      desc:  i18n.t(`appointmentsModule.config.${c.descKey}`),
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language]
  );

  // Carga inicial: perfil
  useEffect(() => {
    (async () => {
      try {
        const { data } = await getOnboardingConfig();
        if (data?.business) {
          if (data.business.profileName) setProfile(data.business.profileName);
          if (data.business.profileId != null) setProfileId(data.business.profileId);
          setBusinessData({
            businessName: data.business.businessName || "",
            profileId:    data.business.profileId ?? null,
            profileName:  data.business.profileName || "",
            timezone:     data.business.timezone || BUSINESS_DIALOG_EMPTY.timezone,
            currency:     data.business.currency || BUSINESS_DIALOG_EMPTY.currency,
          });
        }
      } catch { /* no bloquear */ }
      finally { setLoading(false); }
    })();
  }, []);

  // Servicios
  const fetchServicesData = useCallback(async () => {
    setServicesLoading(true);
    try {
      const { data } = await getOnboardingConfig();
      setServices(data?.services || []);
    } catch (e) { toastError(e); }
    finally { setServicesLoading(false); }
  }, []);

  useEffect(() => {
    if (submenuIndex === 0) fetchServicesData();
  }, [submenuIndex, fetchServicesData]);

  // Equipo
  const fetchEquipoData = useCallback(async () => {
    setEquipoLoading(true);
    try {
      const [configRes, usersRes] = await Promise.all([
        getOnboardingConfig(),
        listUsersSimple(),
      ]);
      setProfessionals(configRes.data?.professionals || []);
      setUsers(usersRes.data || []);
    } catch (e) { toastError(e); }
    finally { setEquipoLoading(false); }
  }, []);

  useEffect(() => {
    if (submenuIndex === 1) fetchEquipoData();
  }, [submenuIndex, fetchEquipoData]);

  // Horarios
  const fetchHorariosData = useCallback(async () => {
    setHorariosLoading(true);
    try {
      const { data } = await getOnboardingConfig();
      const raw = data?.businessHours || [];
      const sorted = [...raw].sort((a, b) => a.dayOfWeek - b.dayOfWeek);
      const normalized = sorted.map(h => ({
        ...h,
        openTime:  (h.openTime  || "09:00").slice(0, 5),
        closeTime: (h.closeTime || "18:00").slice(0, 5),
      }));
      setHorariosLocal(normalized.length ? normalized : DEFAULT_DAYS);
      if (normalized.length) setGlobalSlot(normalized[0].slotDuration || 60);
      setHorariosModified(false);
    } catch (e) { toastError(e); }
    finally { setHorariosLoading(false); }
  }, []);

  useEffect(() => {
    if (submenuIndex === 2) fetchHorariosData();
  }, [submenuIndex, fetchHorariosData]);

  const handleDayToggle = (idx, value) => {
    const next = [...horariosLocal];
    next[idx] = { ...next[idx], isOpen: value };
    setHorariosLocal(next);
    setHorariosModified(true);
  };

  const handleTimeChange = (idx, field, value) => {
    const next = [...horariosLocal];
    next[idx] = { ...next[idx], [field]: value };
    setHorariosLocal(next);
    setHorariosModified(true);
  };

  const handleSaveHorarios = async () => {
    setHorariosSaving(true);
    try {
      const payload = horariosLocal.map(h => ({ ...h, slotDuration: globalSlot }));
      await updateOnboardingBusinessHours({ businessHours: payload });
      toast.success(i18n.t("appointmentsModule.config.horariosToastOk"));
      setHorariosModified(false);
    } catch (e) { toastError(e); }
    finally { setHorariosSaving(false); }
  };

  // Secuencias
  const fetchSequences = useCallback(async () => {
    setSeqLoading(true);
    try {
      const { data } = await listOnboardingSequences();
      setSequences(data || []);
    } catch (e) { toastError(e); }
    finally { setSeqLoading(false); }
  }, []);

  useEffect(() => {
    if (submenuIndex === 3 || submenuIndex === 4) fetchSequences();
  }, [submenuIndex, fetchSequences]);

  const handleToggleSequence = async (seq) => {
    const next = !seq.isEnabled;
    setSequences(prev => prev.map(s => s.id === seq.id ? { ...s, isEnabled: next } : s));
    try {
      await updateOnboardingSequenceByKey(seq.sequenceKey, { isEnabled: next });
      toast.success(next
        ? i18n.t("appointmentsModule.config.secuenciasToastOn")
        : i18n.t("appointmentsModule.config.secuenciasToastOff")
      );
    } catch (e) {
      setSequences(prev => prev.map(s => s.id === seq.id ? { ...s, isEnabled: !next } : s));
      toastError(e);
    }
  };

  // CRUD secuencia
  const handleOpenCreateSeq = () => {
    setSeqDialogMode("create");
    setSeqDialogData(SEQ_DIALOG_EMPTY);
    setSeqDialogOpen(true);
  };

  const handleOpenEditSeq = (seq) => {
    setSeqDialogMode("edit");
    setSeqDialogData({
      id:               seq.id,
      name:             seq.name,
      description:      seq.description || "",
      category:         seq.category || "Recordatorio",
      triggerValue:     seq.triggerValue ?? 1,
      triggerUnit:      seq.triggerUnit || "hour",
      triggerDirection: seq.triggerDirection || "before",
      messageTemplate:  seq.messageTemplate || "",
    });
    setSeqDialogOpen(true);
  };

  const handleSaveSeqDialog = async () => {
    if (!seqDialogData.name.trim()) return;
    setSeqDialogSaving(true);
    try {
      if (seqDialogMode === "create") {
        const { data } = await createOnboardingSequence(seqDialogData);
        setSequences(prev => [...prev, data]);
        toast.success(i18n.t("appointmentsModule.config.seqToastCreated"));
      } else {
        const { data } = await updateOnboardingSequence(seqDialogData.id, seqDialogData);
        setSequences(prev => prev.map(s => s.id === data.id ? data : s));
        toast.success(i18n.t("appointmentsModule.config.seqToastUpdated"));
      }
      setSeqDialogOpen(false);
    } catch (e) { toastError(e); }
    finally { setSeqDialogSaving(false); }
  };

  const handleConfirmDeleteSeq = async () => {
    if (!seqDeleteTarget) return;
    try {
      await deleteOnboardingSequence(seqDeleteTarget.id);
      setSequences(prev => prev.filter(s => s.id !== seqDeleteTarget.id));
      toast.success(i18n.t("appointmentsModule.config.seqToastDeleted"));
      setSeqDeleteTarget(null);
    } catch (e) { toastError(e); }
  };

  // CRUD servicio
  const handleOpenCreateSvc = () => {
    setSvcDialogMode("create");
    setSvcDialogData(SVC_DIALOG_EMPTY);
    setSvcDialogOpen(true);
  };

  const handleOpenEditSvc = (svc) => {
    setSvcDialogMode("edit");
    setSvcDialogData({
      id:                   svc.id,
      serviceName:          svc.serviceName || "",
      description:          svc.description || "",
      durationMin:          svc.durationMin ?? 60,
      price:                svc.price != null ? String(svc.price) : "",
      requiresProfessional: svc.requiresProfessional !== false,
    });
    setSvcDialogOpen(true);
  };

  const handleSaveServiceDialog = async () => {
    if (!svcDialogData.serviceName.trim()) return;
    setSvcDialogSaving(true);
    try {
      const payload = {
        serviceName:          svcDialogData.serviceName.trim(),
        description:          svcDialogData.description.trim(),
        durationMin:          Number(svcDialogData.durationMin) || 60,
        price:                svcDialogData.price !== "" ? Number(svcDialogData.price) : null,
        requiresProfessional: svcDialogData.requiresProfessional,
      };
      if (svcDialogMode === "create") {
        const { data } = await createOnboardingService(payload);
        setServices(prev => [...prev, data]);
        toast.success("Servicio creado correctamente");
      } else {
        const { data } = await updateOnboardingService(svcDialogData.id, payload);
        setServices(prev => prev.map(s => s.id === data.id ? data : s));
        toast.success("Servicio actualizado correctamente");
      }
      setSvcDialogOpen(false);
    } catch (e) { toastError(e); }
    finally { setSvcDialogSaving(false); }
  };

  const handleConfirmDeleteSvc = async () => {
    if (!svcDeleteTarget) return;
    try {
      await deleteOnboardingService(svcDeleteTarget.id);
      setServices(prev => prev.filter(s => s.id !== svcDeleteTarget.id));
      toast.success("Servicio eliminado");
      setSvcDeleteTarget(null);
    } catch (e) { toastError(e); }
  };

  // CRUD profesional
  const handleOpenCreateProf = () => {
    setProfDialogMode("create");
    setProfDialogData(PROF_DIALOG_EMPTY);
    setProfDialogOpen(true);
  };

  const handleOpenEditProf = (prof) => {
    setProfDialogMode("edit");
    setProfDialogData({
      id:             prof.id,
      name:           prof.name || "",
      roleLabel:      prof.roleLabel || "",
      specialty:      prof.specialty || "",
      whatsappUserId: prof.whatsappUserId ?? "",
    });
    setProfDialogOpen(true);
  };

  const handleSaveProfDialog = async () => {
    if (!profDialogData.name.trim()) return;
    setProfDialogSaving(true);
    try {
      const payload = {
        name:           profDialogData.name.trim(),
        roleLabel:      profDialogData.roleLabel.trim(),
        specialty:      profDialogData.specialty.trim(),
        whatsappUserId: profDialogData.whatsappUserId !== "" ? Number(profDialogData.whatsappUserId) : null,
      };
      if (profDialogMode === "create") {
        const { data } = await createOnboardingProfessional(payload);
        setProfessionals(prev => [...prev, data]);
        toast.success("Profesional agregado correctamente");
      } else {
        const { data } = await updateOnboardingProfessional(profDialogData.id, payload);
        setProfessionals(prev => prev.map(p => p.id === data.id ? data : p));
        toast.success("Profesional actualizado correctamente");
      }
      setProfDialogOpen(false);
    } catch (e) { toastError(e); }
    finally { setProfDialogSaving(false); }
  };

  const handleConfirmDeleteProf = async () => {
    if (!profDeleteTarget) return;
    try {
      await deleteOnboardingProfessional(profDeleteTarget.id);
      setProfessionals(prev => prev.filter(p => p.id !== profDeleteTarget.id));
      toast.success("Profesional eliminado");
      setProfDeleteTarget(null);
    } catch (e) { toastError(e); }
  };

  // Handlers generales
  const handleLinkUser = async (professionalId, userId) => {
    try {
      await updateOnboardingProfessionalUser(professionalId, { userId: userId || null });
      toast.success(i18n.t("appointmentsModule.config.toastLinkOk"));
      const { data } = await getOnboardingConfig();
      setProfessionals(data?.professionals || []);
    } catch (e) { toastError(e); }
  };

  /**
   * Edición de los datos del negocio. Usa PATCH /onboarding/config (solo UPDATE).
   * WHY: antes esto navegaba a /onboarding, cuyo guardado final (POST /onboarding/complete)
   * hace destroy+create de Professionals y ServicesCatalog; con las FK ON DELETE SET NULL
   * eso vaciaba professionalId/serviceId de las citas existentes sin avisar.
   */
  const handleOpenBusinessDialog  = () => setBusinessDialogOpen(true);
  const handleCloseBusinessDialog = () => { if (!businessDialogSaving) setBusinessDialogOpen(false); };

  const handleSaveBusinessData = async (form) => {
    setBusinessDialogSaving(true);
    try {
      const { data } = await patchOnboardingConfig({
        businessName: form.businessName,
        profileId:    form.profileId,
        profileName:  form.profileName,
        timezone:     form.timezone,
        currency:     form.currency,
      });
      const saved = data?.business;
      if (saved) {
        setProfile(saved.profileName || "");
        setProfileId(saved.profileId ?? null);
        setBusinessData({
          businessName: saved.businessName || "",
          profileId:    saved.profileId ?? null,
          profileName:  saved.profileName || "",
          timezone:     saved.timezone || BUSINESS_DIALOG_EMPTY.timezone,
          currency:     saved.currency || BUSINESS_DIALOG_EMPTY.currency,
        });
      }
      toast.success(i18n.t("appointmentsModule.config.businessDialogSaved"));
      setBusinessDialogOpen(false);
    } catch (e) {
      toastError(e);
    } finally {
      setBusinessDialogSaving(false);
    }
  };

  const handleRecrearKanban = async () => {
    const pid = profileId ?? 1;
    if (pid < 1 || pid > 5) {
      toast.error(i18n.t("appointmentsModule.config.errorProfileFirst"));
      return;
    }
    setRecrearKanbanLoading(true);
    try {
      await activateOnboarding({ profileId: pid });
      toast.success(i18n.t("appointmentsModule.config.toastKanbanOk"));
    } catch (e) { toastError(e); }
    finally { setRecrearKanbanLoading(false); }
  };

  const currentCard = configCards[submenuIndex];

  return {
    configCards,
    currentCard,
    equipoLoading,
    fetchEquipoData,
    fetchHorariosData,
    fetchSequences,
    fetchServicesData,
    globalSlot,
    businessData,
    businessDialogOpen,
    businessDialogSaving,
    handleOpenBusinessDialog,
    handleCloseBusinessDialog,
    handleSaveBusinessData,
    handleConfirmDeleteProf,
    handleConfirmDeleteSeq,
    handleConfirmDeleteSvc,
    handleDayToggle,
    handleLinkUser,
    handleOpenCreateProf,
    handleOpenCreateSeq,
    handleOpenCreateSvc,
    handleOpenEditProf,
    handleOpenEditSeq,
    handleOpenEditSvc,
    handleRecrearKanban,
    handleSaveHorarios,
    handleSaveProfDialog,
    handleSaveSeqDialog,
    handleSaveServiceDialog,
    handleTimeChange,
    handleToggleSequence,
    history,
    horariosLoading,
    horariosLocal,
    horariosModified,
    horariosSaving,
    loading,
    profDeleteTarget,
    profDialogData,
    profDialogMode,
    profDialogOpen,
    profDialogSaving,
    professionals,
    profile,
    profileId,
    recrearKanbanLoading,
    seqDeleteTarget,
    seqDialogData,
    seqDialogMode,
    seqDialogOpen,
    seqDialogSaving,
    seqLoading,
    sequences,
    services,
    servicesLoading,
    setEquipoLoading,
    setGlobalSlot,
    setHorariosLoading,
    setHorariosLocal,
    setHorariosModified,
    setHorariosSaving,
    setLoading,
    setProfDeleteTarget,
    setProfDialogData,
    setProfDialogMode,
    setProfDialogOpen,
    setProfDialogSaving,
    setProfessionals,
    setProfile,
    setProfileId,
    setRecrearKanbanLoading,
    setSeqDeleteTarget,
    setSeqDialogData,
    setSeqDialogMode,
    setSeqDialogOpen,
    setSeqDialogSaving,
    setSeqLoading,
    setSequences,
    setServices,
    setServicesLoading,
    setSubmenuIndex,
    setSvcDeleteTarget,
    setSvcDialogData,
    setSvcDialogMode,
    setSvcDialogOpen,
    setSvcDialogSaving,
    setUsers,
    submenuIndex,
    svcDeleteTarget,
    svcDialogData,
    svcDialogMode,
    svcDialogOpen,
    svcDialogSaving,
    users,
  };
}
